<script setup>
import { ref } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useLookup, useDefaultGridOptions, useDefaultExport, refreshGridAsync } from '@/composables/data-grid'

import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { chatActionServiceEnum } from '@/stores/chat.store'

import { useChips } from '@/composables/chips'
import { useConfirmedChatActions } from '@/composables/chat-actions'
import { useProjectsColumn } from '@/composables/data-grid-column'
import { useDataGridWidget } from '@/composables/data-grid-toolbar'
import { useDetectDevice } from '@/composables/device'
import { expenseClaimReport, formatExpenseMoney, useExpenseClaimActions } from '@/composables/expense-claim'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import ExpenseClaimDialog from '@/components/accounting/ExpenseClaimDialog.vue'
import MyExpensesGrid from '@/components/accounting/MyExpensesGrid.vue'
import ReportFileDialog from '@/components/accounting/ReportFileDialog.vue'

const props = defineProps({
  scope: {
    type: Number,
    required: true,
  },
  staff: {
    type: Object,
    default: null,
  },
})

const i18n = getI18n()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()
const dataGridWidget = useDataGridWidget()
const { gridRef } = useGridRef()
const { getExpenseClaimStatusChips } = useChips()
const { canCreate, canApprove, canRevertApproval, canPay, canSubmit, canDelete, approveAsync, rejectAsync, revertApprovalAsync, submitAsync, payAsync } = useExpenseClaimActions()
const { expenseClaimScopeEnum } = accountingStore
const { exportOptions, onExporting } = useDefaultExport({ fileName: i18n.t('common.expenseClaims') })

const isStaffScope = props.scope === expenseClaimScopeEnum.STAFF && !props.staff
const openedClaim = ref(null)
const actionsMenu = ref(null)
const claimToDelete = ref(null)
const printedClaims = ref([])
const { isMobile } = useDetectDevice()
const canManagePayments = webSiteStore.can(webSiteStore.permissionFlagEnum.REIMBURSEMENTS)

const isUnclaimedRow = (row) => !!row?.is_unclaimed

const dataSource = useODataSource(accountingStore.api.getAbsoluteUrl() + accountingStore.api.expenseClaimsUrl, {
  customParamsFn: () => ({ scope: props.scope, with_unclaimed: true, unclaimed_by_staff: webSiteStore.expenseClaimsUnclaimedByStaff, ...(props.staff && { staff: props.staff.id }) }),
})

const moneyColumn = (dataField, caption) => ({
  dataField,
  caption,
  dataType: 'number',
  allowFiltering: false,
  allowSorting: false,
  calculateDisplayValue: (row) => formatExpenseMoney(row[dataField]),
})

const columns = [
  {
    type: 'buttons',
    buttons: [
      { hint: i18n.t('common.edit'), icon: 'edit', visible: (e) => !isUnclaimedRow(e.row.data), onClick: (e) => { openedClaim.value = e.row.data } },
      { hint: i18n.t('common.delete'), icon: 'trash', visible: (e) => !isUnclaimedRow(e.row.data) && canDelete(e.row.data), onClick: (e) => { claimToDelete.value = e.row.data } },
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '5rem',
  },
  { dataField: 'number', caption: i18n.t('common.number'), cellTemplate: 'number-cell', sortOrder: 'desc', sortIndex: 2, width: '12rem' },
  { dataField: 'staff_label', caption: i18n.t('common.staff'), visible: !props.staff },
  { dataField: 'date_from', caption: i18n.t('common.period'), dataType: 'date', sortOrder: 'desc', sortIndex: 1 },
  {
    dataField: 'status',
    caption: i18n.t('common.status'),
    cellTemplate: 'status-cell',
    lookup: useLookup({ dataSource: accountingStore.getLocalizedExpenseClaimStatuses(), valueExpr: 'id', displayExpr: 'name' }),
    ...(isStaffScope ? {} : { sortOrder: 'asc', sortIndex: 0 }),
  },
  useProjectsColumn({ dataType: 'string', allowFiltering: true, filterOperations: ['contains'] }),
  { dataField: 'rows_count', caption: i18n.t('common.expenseItems'), dataType: 'number', allowFiltering: false, allowSorting: false, width: '7rem' },
  moneyColumn('total_reference', i18n.t('common.expenseClaimTotal')),
  { dataField: 'created_at', caption: i18n.t('common.createdAt'), dataType: 'date' },
  { dataField: 'created_by_label', caption: i18n.t('common.createdBy'), allowFiltering: false, allowSorting: false, visible: isStaffScope },
  { dataField: 'approved_by_label', caption: i18n.t('common.expenseClaimApprovedBy'), allowFiltering: false, allowSorting: false },
  { dataField: 'updated_at', caption: i18n.t('common.updatedAt'), dataType: 'date' },
  { dataField: 'updated_by_label', caption: i18n.t('common.updatedBy'), allowFiltering: false, allowSorting: false },
]

const selectionAction = (claims, text, icon, isAllowed, transition) => ({
  text,
  icon,
  disabled: !claims.length || !claims.every(isAllowed),
  onClick: () => onTransitionAsync(() => transition(claims)),
})

const buildActions = (claims) => [
  selectionAction(claims, i18n.t('common.submitForApproval'), 'mdi mdi-clipboard-check-outline', canSubmit, submitAsync),
  ...(webSiteStore.canApproveExpenseClaims ? [
    selectionAction(claims, i18n.t('common.approve'), 'mdi mdi-check', canApprove, approveAsync),
    { ...selectionAction(claims, i18n.t('common.reject'), 'mdi mdi-close-circle-outline', canApprove, ([claim]) => rejectAsync(claim)), disabled: claims.length !== 1 || !canApprove(claims[0]) },
    selectionAction(claims, i18n.t('common.restore'), 'mdi mdi-undo-variant', canRevertApproval, revertApprovalAsync),
  ] : []),
  ...(canManagePayments ? [selectionAction(claims, i18n.t('common.expenseClaimMarkPaid'), 'mdi mdi-cash-check', canPay, payAsync)] : []),
]

const onSelectionChanged = (e) => {
  const unclaimedKeys = e.selectedRowsData.filter(isUnclaimedRow).map(row => row.id)
  if (unclaimedKeys.length) {
    e.component.deselectRows(unclaimedKeys)
    return
  }
  actionsMenu.value?.option('items', buildActions(e.selectedRowsData))
  dataGridWidget.printWidgetButton.value?.option('disabled', !e.selectedRowsData.length)
}

const onUnclaimedByStaffChange = (value) => {
  webSiteStore.expenseClaimsUnclaimedByStaff = value
  refreshAsync()
}

const onPrint = () => { printedClaims.value = gridRef.value.instance.getSelectedRowsData() }

const refreshAsync = async(highlightedClaimId = null) => {
  gridRef.value?.instance.clearSelection()
  await refreshGridAsync(gridRef, highlightedClaimId)
}

useConfirmedChatActions(chatActionServiceEnum.EXPENSES, () => refreshAsync())

const openNew = () => {
  const staff = props.staff?.id ?? webSiteStore.staffId
  if (canCreate(staff)) openedClaim.value = { staff }
}

const openClaimAsync = async(claimId) => {
  openedClaim.value = await accountingStore.fetchExpenseClaimByIdAsync(claimId)
}

const onClaimSaved = async(claim) => {
  openedClaim.value = null
  await refreshAsync(claim?.id)
}

const onTransitionAsync = async(transition) => {
  if (await transition()) await refreshAsync()
}

const onDeleteConfirmedAsync = async() => {
  await accountingStore.deleteExpenseClaimAsync(claimToDelete.value.id)
  claimToDelete.value = null
  await refreshAsync()
}

const onClaimChanged = async() => {
  openedClaim.value = null
  await refreshAsync()
}

const gridOptions = useDefaultGridOptions({
  id: props.staff ? 'staff-expense-claims-grid-v2' : isStaffScope ? 'expense-claims-grid-v5' : 'my-expense-claims-grid-v5',
  dataSource,
  columns,
  editing: { allowAdding: false, allowUpdating: false, allowDeleting: false },
  export: exportOptions,
  onExporting,
  clearFilterButton: true,
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  onSelectionChanged,
  onCellPrepared: (e) => {
    if (e.rowType === 'data' && e.column.command === 'select' && isUnclaimedRow(e.data)) {
      e.cellElement.querySelector('.dx-select-checkbox')?.classList.add('invisible')
    }
  },
  onRowDblClick: (e) => {
    if (isUnclaimedRow(e.data)) e.component.expandRow(e.key)
    else openedClaim.value = e.data
  },
  addButton: { onClick: openNew },
  isDetailGrid: !!props.staff,
  exportWidgets: [
    dataGridWidget.printWidget({ onClick: onPrint }),
    ...(buildActions([]).length ? [dataGridWidget.actionsMenuWidget(actionsMenu, { items: buildActions([]) })] : []),
  ],
  masterDetail: { enabled: true, template: 'master-detail' },
  viewToolbarItems: isStaffScope && webSiteStore.canApproveExpenseClaims ? [{ location: 'after', template: 'unclaimed-by-staff-toggle' }] : [],
})

defineExpose({ refreshAsync, openNew, openClaimAsync })
</script>

<template>
  <div class="column no-wrap">
    <confirm-dialog
      :model-value="claimToDelete !== null"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @update:model-value="value => { if (!value) claimToDelete = null }"
      @confirm="onDeleteConfirmedAsync"
    />
    <q-dialog :model-value="printedClaims.length > 0" :maximized="isMobile" @hide="printedClaims = []">
      <report-file-dialog
        v-if="printedClaims.length"
        :report="expenseClaimReport(printedClaims[0])"
        :report-ids="printedClaims.map(claim => claim.report)"
        @cancel="printedClaims = []"
      />
    </q-dialog>
    <expense-claim-dialog
      :claim="openedClaim"
      @saved="onClaimSaved"
      @changed="onClaimChanged"
      @cancel="openedClaim = null"
    />
    <dx-data-grid ref="gridRef" v-bind="gridOptions">
      <template #number-cell="{ data: templateOptions }">
        <span class="row items-center no-wrap q-gutter-x-xs">
          <q-icon
            v-if="isUnclaimedRow(templateOptions.data)"
            name="mdi-tray-full"
            color="warning"
            size="xs"
          />
          <span>{{ templateOptions.data.number }}</span>
        </span>
      </template>
      <template #unclaimed-by-staff-toggle>
        <q-toggle
          :model-value="webSiteStore.expenseClaimsUnclaimedByStaff"
          :label="i18n.t('common.expensesWithoutClaimByStaff')"
          color="primary"
          @update:model-value="onUnclaimedByStaffChange"
        />
      </template>
      <template #status-cell="{ data: templateOptions }">
        <chip-template :values="getExpenseClaimStatusChips(templateOptions.data)" />
      </template>
      <template #master-detail="{ data: templateData }">
        <my-expenses-grid
          v-if="isUnclaimedRow(templateData.data)"
          :staff="templateData.data.staff ? { id: templateData.data.staff } : null"
          @row-saved="refreshAsync()"
          @claim-saved="claim => refreshAsync(claim.id)"
        />
        <my-expenses-grid
          v-else
          :expense-claim="templateData.data"
          @row-saved="refreshAsync()"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
